import { useState } from 'react';
import { cn } from '@/lib/cn';
import { Button } from '@/ui/Button';
import { Card } from '@/ui/Card';
import { SkeletonTarjetas } from '@/ui/Skeleton';
import { BarraDeProgreso } from '@/components/BarraDeProgreso';
import { EstadoError } from '@/components/EstadoError';
import { EstadoVacio } from '@/components/EstadoVacio';
import { IconoDePortada, PildorasDeFiltro } from '../piezas';
import { DetalleDeRuta } from './DetalleDeRuta';
import { NuevaRuta } from './FormularioDeRuta';
import { useRutas, type RutaResumen } from './api';

/**
 * Rutas — `#rutas` y `#rutas/<id>`. Módulo 11, opción 3.
 *
 * Una sola entrada del PAGE_MAP para las dos vistas, igual que el vanilla: sin
 * nada detrás, la lista; con un id, el detalle.
 *
 * ## Lo que cambia en la lista
 *
 * **Un fallo al cargar era un callejón**: un `alert-error` con el mensaje y nada
 * más. Ahora dice qué pasó y deja reintentar.
 *
 * **Archivar era un viaje de ida.** A quien gestiona, el servidor le devuelve
 * borradores y publicadas, y las archivadas solo si las pide. El vanilla nunca
 * las pedía: una ruta archivada desaparecía de la lista y la única forma de
 * volver a ella —para restaurarla— era saberse su número. Ahora es un filtro.
 *
 * **Las insignias de la tarjeta** iban con `rgba()` escrito a mano; el icono
 * guardado, con `IconoDePortada`, porque la única ruta que hay guarda un emoji.
 */
export function Rutas() {
  // La sub-ruta se lee UNA vez: el router desmonta y vuelve a montar la capa
  // React en cada `hashchange`, así que otra ruta ya es otro montaje.
  const [param] = useState(() => window._routeParams?.[0] ?? null);

  if (param == null || param === '') return <ListaDeRutas />;
  if (!/^\d+$/.test(param)) {
    return (
      <EstadoError
        err={new Error(`«${param}» no es una ruta de aprendizaje.`)}
        que="la ruta"
        onVolver={() => APP.navigate('learning-paths')}
      />
    );
  }
  return <DetalleDeRuta id={Number(param)} />;
}

function ListaDeRutas() {
  // `cursos:manage` en el servidor, igual que el catálogo y el detalle.
  const puedeGestionar = Auth.can('cursos', 'manage') || Auth.isStaff();
  const [archivadas, setArchivadas] = useState(false);
  const rutas = useRutas(archivadas);
  const [creando, setCreando] = useState(false);

  return (
    <>
      <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-6 font-bold text-brand-ink">
            <i className="bi bi-signpost-split-fill" aria-hidden="true" />
            Rutas de aprendizaje
          </h1>
          <p className="mt-1 text-3 text-muted">
            Secuencias de cursos por cargo, área o proceso. Complétalas en orden para avanzar.
          </p>
        </div>
        {puedeGestionar && (
          <Button size="sm" onClick={() => setCreando(true)}>
            <i className="bi bi-plus-circle-fill" aria-hidden="true" /> Nueva ruta
          </Button>
        )}
      </header>

      {puedeGestionar && (
        <PildorasDeFiltro
          etiqueta="Qué rutas ver"
          activa={archivadas ? 'archivadas' : 'vigentes'}
          onElegir={(v) => setArchivadas(v === 'archivadas')}
          opciones={[
            { valor: 'vigentes', texto: 'Publicadas y borradores', icono: 'bi-signpost-split' },
            { valor: 'archivadas', texto: 'Archivadas', icono: 'bi-archive' },
          ]}
        />
      )}

      {rutas.isLoading ? (
        <SkeletonTarjetas n={6} />
      ) : rutas.isError ? (
        <EstadoError err={rutas.error} que="las rutas" onReintentar={() => void rutas.refetch()} />
      ) : !rutas.data?.length ? (
        archivadas ? (
          <EstadoVacio
            icono="bi-archive"
            titulo="No hay rutas archivadas"
            descripcion="Al archivar una ruta, el equipo deja de verla y aparece aquí para poder restaurarla."
            compacto
          />
        ) : (
          <EstadoVacio
            icono="bi-signpost-2"
            titulo="Todavía no hay rutas"
            descripcion={
              puedeGestionar
                ? 'Crea la primera para agrupar cursos en un plan de formación.'
                : 'Pronto encontrarás aquí tus planes de formación.'
            }
            accion={puedeGestionar ? { texto: 'Nueva ruta', onClick: () => setCreando(true) } : undefined}
          />
        )
      ) : (
        <ul className="grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(232px,1fr))]">
          {rutas.data.map((r) => (
            <li key={r.id} className="flex">
              <TarjetaDeRuta r={r} />
            </li>
          ))}
        </ul>
      )}

      {puedeGestionar && <NuevaRuta abierta={creando} onCerrar={() => setCreando(false)} />}
    </>
  );
}

/**
 * Una ruta en la rejilla. Un `<a>` con `href` de verdad que cubre la tarjeta,
 * como la del catálogo: se tabula, se abre con Enter y en otra pestaña.
 */
function TarjetaDeRuta({ r }: { r: RutaResumen }) {
  const pct = Math.round(r.progress_pct || 0);
  const total = r.required_count || r.course_count;
  const estado = r.status && r.status !== 'publicado' ? (r.status === 'borrador' ? 'Borrador' : 'Archivada') : null;

  return (
    <Card interactiva className="relative flex w-full flex-col overflow-hidden">
      <div
        className="relative flex h-28 shrink-0 items-center justify-center"
        style={{ background: r.cover_icon_color || '#0D2B4E' }}
      >
        <IconoDePortada valor={r.cover_icon} respaldo="bi-signpost-split" className="text-8 leading-none text-white" />

        {r.is_mandatory && (
          <span className="absolute left-2 top-2 rounded-xs bg-black/55 px-1.5 py-0.5 text-1 font-semibold text-white">
            Obligatoria
          </span>
        )}
        {estado && (
          <span className="absolute bottom-2 left-2 rounded-xs bg-black/55 px-1.5 py-0.5 text-1 font-semibold text-white">
            {estado}
          </span>
        )}
        {r.completed && (
          <span
            className="absolute right-2 top-2 flex size-6 items-center justify-center rounded-full bg-ok text-white"
            title="Completada"
          >
            <i className="bi bi-check-lg" aria-hidden="true" />
            <span className="sr-only">Completada</span>
          </span>
        )}
      </div>

      <div className="flex flex-1 flex-col gap-2 p-3.5">
        <h3 className="text-3 font-bold leading-snug text-brand-ink [overflow-wrap:anywhere]">
          <a
            href={`#rutas/${r.id}`}
            className="rounded-xs text-inherit no-underline after:absolute after:inset-0 focus-visible:focus-anillo"
            aria-label={`${r.title} — ${r.completed ? 'completada' : `${pct} % completada`}`}
          >
            {r.title}
          </a>
        </h3>

        {r.description && (
          <p className="line-clamp-2 text-2 text-muted [overflow-wrap:anywhere]">{r.description}</p>
        )}

        <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-2 text-ink-2">
          <span className="flex items-center gap-1.5">
            <i className="bi bi-collection-play" aria-hidden="true" />
            {r.course_count} {r.course_count === 1 ? 'curso' : 'cursos'}
          </span>
          <span className="ml-auto text-1 tabular-nums text-muted">
            {r.completed_count}/{total} completados
          </span>
        </p>

        <div className="mt-auto flex items-center gap-2 pt-1">
          <div className="flex-1">
            <BarraDeProgreso pct={pct} etiqueta="Avance de la ruta" />
          </div>
          <span className={cn('text-1 font-bold tabular-nums', r.completed ? 'text-ok-ink' : 'text-ink-2')}>
            {pct}%
          </span>
        </div>
      </div>
    </Card>
  );
}
